// src/phase4/data/wave-configs.ts
// Wave configuration data

import type { WaveConfigSet, WaveConfig } from '../types/wave';
import { EnemyType } from '../types/enemy';

// Individual wave configurations
export const WAVE_CONFIGS: WaveConfig[] = [
  // Wave 1: Introduction - just SQL injections
  {
    waveNumber: 1,
    difficultyMultiplier: 1.0,
    timeBetweenSpawns: 1500,
    spawnDelay: 1000,
    enemySpawns: [
      { enemyType: EnemyType.SQL_INJECTION, position: 'front', delayAfter: 0 },
      { enemyType: EnemyType.SQL_INJECTION, position: 'mid', delayAfter: 500 },
      { enemyType: EnemyType.SQL_INJECTION, position: 'back', delayAfter: 1000 },
    ],
  },
  // Wave 2: XSS introduction
  {
    waveNumber: 2,
    difficultyMultiplier: 1.3,
    timeBetweenSpawns: 1200,
    spawnDelay: 2000,
    enemySpawns: [
      { enemyType: EnemyType.SQL_INJECTION, position: 'front', delayAfter: 0 },
      { enemyType: EnemyType.XSS_ATTACK, position: 'mid', delayAfter: 800 },
      { enemyType: EnemyType.SQL_INJECTION, position: 'back', delayAfter: 1600 },
      { enemyType: EnemyType.XSS_ATTACK, position: 'mid', delayAfter: 2400 },
    ],
  },
  // Wave 3: DDoS bots introduced
  {
    waveNumber: 3,
    difficultyMultiplier: 1.6,
    timeBetweenSpawns: 1000,
    spawnDelay: 2000,
    enemySpawns: [
      { enemyType: EnemyType.SQL_INJECTION, position: 'front', delayAfter: 0 },
      { enemyType: EnemyType.XSS_ATTACK, position: 'front', delayAfter: 400 },
      { enemyType: EnemyType.DDOS_BOT, position: 'mid', delayAfter: 1000 },
      { enemyType: EnemyType.SQL_INJECTION, position: 'back', delayAfter: 1800 },
      { enemyType: EnemyType.XSS_ATTACK, position: 'back', delayAfter: 2200 },
    ],
  },
  // Wave 4: Mixed forces
  {
    waveNumber: 4,
    difficultyMultiplier: 2.0,
    timeBetweenSpawns: 900,
    spawnDelay: 2500,
    enemySpawns: [
      { enemyType: EnemyType.SQL_INJECTION, position: 'front', delayAfter: 0 },
      { enemyType: EnemyType.DDOS_BOT, position: 'front', delayAfter: 500 },
      { enemyType: EnemyType.XSS_ATTACK, position: 'mid', delayAfter: 1200 },
      { enemyType: EnemyType.SQL_INJECTION, position: 'mid', delayAfter: 1600 },
      { enemyType: EnemyType.DDOS_BOT, position: 'back', delayAfter: 2400 },
      { enemyType: EnemyType.XSS_ATTACK, position: 'back', delayAfter: 2800 },
    ],
  },
  // Wave 5: Trojan introduction
  {
    waveNumber: 5,
    difficultyMultiplier: 2.4,
    timeBetweenSpawns: 800,
    spawnDelay: 2500,
    specialMechanic: 'stealth_wave',
    enemySpawns: [
      { enemyType: EnemyType.TROJAN, position: 'front', delayAfter: 0 },
      { enemyType: EnemyType.SQL_INJECTION, position: 'front', delayAfter: 600 },
      { enemyType: EnemyType.TROJAN, position: 'mid', delayAfter: 1400 },
      { enemyType: EnemyType.XSS_ATTACK, position: 'mid', delayAfter: 2000 },
      { enemyType: EnemyType.DDOS_BOT, position: 'back', delayAfter: 2800 },
    ],
  },
  // Wave 6: Boss wave - Zero-Day
  {
    waveNumber: 6,
    difficultyMultiplier: 3.0,
    timeBetweenSpawns: 600,
    spawnDelay: 3000,
    specialMechanic: 'boss_wave',
    enemySpawns: [
      { enemyType: EnemyType.ZERO_DAY, position: 'mid', delayAfter: 0 },
      { enemyType: EnemyType.DDOS_BOT, position: 'front', delayAfter: 1500 },
      { enemyType: EnemyType.DDOS_BOT, position: 'back', delayAfter: 2000 },
      { enemyType: EnemyType.TROJAN, position: 'front', delayAfter: 3000 },
    ],
  },
  // Wave 7+: Endless escalation (template for more waves)
  {
    waveNumber: 7,
    difficultyMultiplier: 3.5,
    timeBetweenSpawns: 500,
    spawnDelay: 2000,
    specialMechanic: 'rush_hour',
    enemySpawns: [
      { enemyType: EnemyType.SQL_INJECTION, position: 'front', delayAfter: 0 },
      { enemyType: EnemyType.XSS_ATTACK, position: 'front', delayAfter: 200 },
      { enemyType: EnemyType.SQL_INJECTION, position: 'mid', delayAfter: 400 },
      { enemyType: EnemyType.XSS_ATTACK, position: 'mid', delayAfter: 600 },
      { enemyType: EnemyType.DDOS_BOT, position: 'back', delayAfter: 800 },
      { enemyType: EnemyType.TROJAN, position: 'back', delayAfter: 1000 },
    ],
  },
];

// Wave configuration set
export const WAVE_CONFIG_SET: WaveConfigSet = {
  totalWaves: WAVE_CONFIGS.length,
  waves: WAVE_CONFIGS,
  baseDifficulty: 1.0,
  difficultyGrowthPerWave: 0.2,
  waveCooldown: 5000, // 5 seconds between waves
};

// Get wave config by wave number
export function getWaveConfig(waveNumber: number): WaveConfig | undefined {
  return WAVE_CONFIGS[waveNumber - 1];
}

// Generate wave configs for higher waves (8+)
export function generateExtendedWaveConfig(waveNumber: number): WaveConfig {
  const difficultyMultiplier = 3.0 + (waveNumber - 6) * 0.3;

  return {
    waveNumber,
    difficultyMultiplier,
    timeBetweenSpawns: Math.max(300, 600 - (waveNumber - 7) * 30),
    spawnDelay: 2000,
    specialMechanic: waveNumber % 5 === 0 ? 'boss_wave' : 'rush_hour',
    enemySpawns: [
      {
        enemyType: waveNumber % 5 === 0 ? EnemyType.ZERO_DAY : EnemyType.TROJAN,
        position: 'mid',
        delayAfter: 0,
      },
      ...Array.from({ length: Math.min(waveNumber, 8) }, (_, i) => ({
        enemyType:
          i % 3 === 0
            ? EnemyType.DDOS_BOT
            : i % 2 === 0
              ? EnemyType.XSS_ATTACK
              : EnemyType.SQL_INJECTION,
        position: (['front', 'mid', 'back'][i % 3]) as 'front' | 'mid' | 'back',
        delayAfter: (i + 1) * 400,
      })),
    ],
  };
}
